import {
  ZadAccountingPaymentMethod,
  ZadAccountingReport,
} from '../../../models/zad'
import {hasZadFinancialAccess} from '../../../roles'
import {toZadIsoDate, ZAD_CASH_FLOW_METHOD_I18N} from './reportHelpers'

export const ZAD_ACCOUNTING_MAX_RANGE_DAYS = 366
export const ZAD_ACCOUNTING_ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export const ZAD_ACCOUNTING_DOCUMENT_TYPE_I18N: Record<string, string> = {
  SALE: 'ZAD.ACCOUNTING.DOC_SALE',
  CUSTOMER_PAYMENT: 'ZAD.ACCOUNTING.DOC_CUSTOMER_PAYMENT',
  PURCHASE: 'ZAD.ACCOUNTING.DOC_PURCHASE',
  SUPPLIER_PAYMENT: 'ZAD.ACCOUNTING.DOC_SUPPLIER_PAYMENT',
  CUSTOMER_RETURN: 'ZAD.ACCOUNTING.DOC_CUSTOMER_RETURN',
  CUSTOMER_REFUND: 'ZAD.ACCOUNTING.DOC_CUSTOMER_REFUND',
  DELIVERY_NOTE: 'ZAD.ACCOUNTING.DOC_DELIVERY_NOTE',
}

export function defaultZadAccountingPeriod(now: Date = new Date()): {from: string; to: string} {
  const from = new Date(now.getFullYear(), now.getMonth(), 1)
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return {from: toZadIsoDate(from), to: toZadIsoDate(to)}
}

export function isZadAccountingIsoDate(value?: string | null): boolean {
  if (!value || !ZAD_ACCOUNTING_ISO_DATE.test(value)) return false
  const [year, month, day] = value.split('-').map(Number)
  const parsed = new Date(year, month - 1, day)
  return (
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day
  )
}

export function zadAccountingInclusiveDays(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00`)
  const end = Date.parse(`${to}T00:00:00`)
  if (Number.isNaN(start) || Number.isNaN(end)) return Number.NaN
  return Math.round((end - start) / 86400000) + 1
}

export type ZadAccountingDateErrorId =
  | 'ZAD.ACCOUNTING.DATE_REQUIRED'
  | 'ZAD.ACCOUNTING.DATE_INVALID'
  | 'ZAD.ACCOUNTING.DATE_ORDER'
  | 'ZAD.ACCOUNTING.DATE_RANGE'

export function validateZadAccountingPeriod(
  from?: string | null,
  to?: string | null
): ZadAccountingDateErrorId | null {
  if (!from || !to) return 'ZAD.ACCOUNTING.DATE_REQUIRED'
  if (!isZadAccountingIsoDate(from) || !isZadAccountingIsoDate(to)) {
    return 'ZAD.ACCOUNTING.DATE_INVALID'
  }
  if (from > to) return 'ZAD.ACCOUNTING.DATE_ORDER'
  if (zadAccountingInclusiveDays(from, to) > ZAD_ACCOUNTING_MAX_RANGE_DAYS) {
    return 'ZAD.ACCOUNTING.DATE_RANGE'
  }
  return null
}

export function buildZadAccountingQuery(params: {from?: string; to?: string}): string {
  const query = new URLSearchParams()
  if (params.from) query.set('from', params.from)
  if (params.to) query.set('to', params.to)
  return query.toString()
}

export function extractZadAccountingReport(payload: unknown): ZadAccountingReport | null {
  if (!payload || typeof payload !== 'object') return null
  const raw = payload as Record<string, unknown>
  const nested =
    raw.report && typeof raw.report === 'object'
      ? (raw.report as Record<string, unknown>)
      : raw.accounting && typeof raw.accounting === 'object'
      ? (raw.accounting as Record<string, unknown>)
      : raw.data && typeof raw.data === 'object' && !(raw as {period?: unknown}).period
      ? (raw.data as Record<string, unknown>)
      : raw
  if (!nested || typeof nested !== 'object') return null
  if (!nested.period && !nested.summary) return null
  const {success: _success, ...report} = nested as ZadAccountingReport & {success?: unknown}
  return report
}

export function zadAccountingPaymentMethodI18n(method?: ZadAccountingPaymentMethod | null): string {
  const key = String(method || '').trim().toUpperCase()
  return ZAD_CASH_FLOW_METHOD_I18N[key as keyof typeof ZAD_CASH_FLOW_METHOD_I18N] ||
    'ZAD.CUSTOMER_PAYMENTS.METHOD_OTHER'
}

export function zadAccountingDocumentTypeI18n(type?: string | null): string {
  const key = String(type || '').trim().toUpperCase()
  return ZAD_ACCOUNTING_DOCUMENT_TYPE_I18N[key] || 'ZAD.ACCOUNTING.DOC_OTHER'
}

export function zadAccountingDash(value?: string | null): string {
  const text = String(value || '').trim()
  return text || '—'
}

export function canShowZadAccountingManagement(
  role?: string | number | null,
  management?: ZadAccountingReport['management'] | null
): boolean {
  if (!hasZadFinancialAccess(role) || !management || typeof management !== 'object') return false
  return (
    management.costOfGoodsSold != null ||
    management.grossMargin != null ||
    Boolean(management.note)
  )
}

export function formatZadAccountingPeriodLabel(from?: string | null, to?: string | null): string {
  const start = from ? String(from).slice(0, 10) : ''
  const end = to ? String(to).slice(0, 10) : ''
  if (start && end) return `${start} → ${end}`
  return start || end
}
